import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BuddyAvatar, BUDDY_ROSTER } from '@/components/BuddyRoster';

const CORNERS = {
  'bottom-right': 'bottom-0 right-0 flex-row-reverse text-right',
  'bottom-left': 'bottom-0 left-0 flex-row text-left',
};
const KEYS = Object.keys(CORNERS);

/**
 * BuddyCorner — the Study Buddy standing in the corner of a dialog.
 *
 * The owner's point, and it's a fair one: a buddy who only lives in a bubble on
 * the right-hand edge isn't a buddy. Whenever we stop a child with a pop-up,
 * the buddy should be *in* it — present, looking at them, saying the thing in
 * their own voice. The corner is picked per dialog so it never feels like a
 * fixed piece of furniture.
 */
export function BuddyCorner({ line, corner, inline = false, mood = 'happy', size = 64 }) {
  const { user } = useAuth();
  const buddyId = (user?.preferred_buddy && BUDDY_ROSTER[user.preferred_buddy])
    ? user.preferred_buddy : 'chirpy';
  const spot = useMemo(
    () => (corner && CORNERS[corner] ? corner : KEYS[Math.floor(Math.random() * KEYS.length)]),
    [corner],
  );
  const buddy = BUDDY_ROSTER[buddyId] || {};

  const body = (
    <>
      <div className="flex-shrink-0 drop-shadow-sm" style={{ width: size, height: size }}>
        <BuddyAvatar buddyId={buddyId} mood={mood} size={size} />
      </div>
      {line && (
        <p className="font-body text-[12px] leading-snug text-slate-600 max-w-[16rem]">
          <span className="font-heading text-[10px] uppercase tracking-widest text-slate-400 block mb-0.5">
            {buddy.name || 'Study Buddy'}
          </span>
          {line}
        </p>
      )}
    </>
  );

  if (inline) {
    return (
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100"
           data-testid="buddy-corner">
        {body}
      </div>
    );
  }

  return (
    <div className={`absolute z-10 flex items-end gap-2 p-4 pointer-events-none ${CORNERS[spot]}`}
         data-testid="buddy-corner">
      {body}
    </div>
  );
}

export default BuddyCorner;
